import React, { useState } from 'react';
import { Button } from '../../../../../ui';
import { CCASubdomain, CCA_SUBDOMAINS } from '../../../../questions/CCABank/ccaDummyData';

type CCAStatus = 'invite-sent' | 'in-progress' | 'completed';

interface CCAFilters {
  totalScoreMin: number | '';
  totalScoreMax: number | '';
  subdomainScores: Record<CCASubdomain, { min: number | ''; max: number | '' }>;
  topX: number | '';
  statuses: CCAStatus[];
}

const STATUS_OPTIONS: { value: CCAStatus; label: string; color: string }[] = [
  { value: 'invite-sent', label: 'Invite Sent', color: 'bg-slate-100 text-slate-700' },
  { value: 'in-progress', label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
];

const defaultFilters = (): CCAFilters => ({
  totalScoreMin: '',
  totalScoreMax: '',
  subdomainScores: Object.fromEntries(
    CCA_SUBDOMAINS.map((sd) => [sd, { min: '', max: '' }]),
  ) as Record<CCASubdomain, { min: number | ''; max: number | '' }>,
  topX: '',
  statuses: [],
});

interface CCAFilterDrawerContentProps {
  onApply: () => void;
}

export const CCAFilterDrawerContent: React.FC<CCAFilterDrawerContentProps> = ({
  onApply,
}) => {
  const [filters, setFilters] = useState<CCAFilters>(defaultFilters());

  const setTotalMin = (v: string) =>
    setFilters((p) => ({ ...p, totalScoreMin: v === '' ? '' : Number(v) }));
  const setTotalMax = (v: string) =>
    setFilters((p) => ({ ...p, totalScoreMax: v === '' ? '' : Number(v) }));
  const setTopX = (v: string) =>
    setFilters((p) => ({ ...p, topX: v === '' ? '' : Number(v) }));

  const setSubdomainMin = (sd: CCASubdomain, v: string) =>
    setFilters((p) => ({
      ...p,
      subdomainScores: {
        ...p.subdomainScores,
        [sd]: { ...p.subdomainScores[sd], min: v === '' ? '' : Number(v) },
      },
    }));

  const setSubdomainMax = (sd: CCASubdomain, v: string) =>
    setFilters((p) => ({
      ...p,
      subdomainScores: {
        ...p.subdomainScores,
        [sd]: { ...p.subdomainScores[sd], max: v === '' ? '' : Number(v) },
      },
    }));

  const toggleStatus = (s: CCAStatus) =>
    setFilters((p) => ({
      ...p,
      statuses: p.statuses.includes(s)
        ? p.statuses.filter((x) => x !== s)
        : [...p.statuses, s],
    }));

  const handleReset = () => setFilters(defaultFilters());

  const inputClass =
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 shadow-sm focus:border-emerald-400 focus:outline-none focus:ring-1 focus:ring-emerald-300';

  const subdomainBorderColors: Record<CCASubdomain, string> = {
    'Abstract Reasoning': 'border-l-violet-400',
    'Logical Reasoning': 'border-l-blue-400',
    'Quantitative Aptitude': 'border-l-emerald-400',
    'Verbal Ability': 'border-l-amber-400',
    'Data Interpretation': 'border-l-rose-400',
  };

  return (
    <div className='flex h-full flex-col'>
      <div className='flex-1 overflow-y-auto space-y-5 pb-4'>
        { /* CCA Status */ }
        <div>
          <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500'>
            CCA Status
          </p>
          <div className='flex flex-wrap gap-2'>
            { STATUS_OPTIONS.map((s) => (
              <button
                  key={s.value}
                  type='button'
                  onClick={() => toggleStatus(s.value)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-all ${
                    filters.statuses.includes(s.value)
                      ? `${s.color} ring-2 ring-offset-1 ring-slate-300`
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
              >
                { s.label }
              </button>
            )) }
          </div>
        </div>

        { /* Top X */ }
        <div>
          <p className='mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500'>
            Top X Candidates by Score
          </p>
          <p className='mb-2 text-[11px] text-slate-400'>
            e.g. enter 10 to show only the top 10 CCA scorers
          </p>
          <input
              type='number'
              min={1}
              max={100}
              placeholder='e.g. 10'
              value={filters.topX}
              onChange={(e) => setTopX(e.target.value)}
              className={inputClass}
          />
        </div>

        { /* Total CCA Score */ }
        <div>
          <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500'>
            Total CCA Score (0–100)
          </p>
          <div className='grid grid-cols-2 gap-2'>
            <div>
              <label className='mb-1 block text-[11px] text-slate-400'>Min</label>
              <input
                  type='number'
                  min={0}
                  max={100}
                  placeholder='Any'
                  value={filters.totalScoreMin}
                  onChange={(e) => setTotalMin(e.target.value)}
                  className={inputClass}
              />
            </div>
            <div>
              <label className='mb-1 block text-[11px] text-slate-400'>Max</label>
              <input
                  type='number'
                  min={0}
                  max={100}
                  placeholder='Any'
                  value={filters.totalScoreMax}
                  onChange={(e) => setTotalMax(e.target.value)}
                  className={inputClass}
              />
            </div>
          </div>
        </div>

        { /* Per-subdomain scores */ }
        <div>
          <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500'>
            Subdomain Scores (0–100)
          </p>
          <div className='space-y-3'>
            { CCA_SUBDOMAINS.map((sd) => (
              <div
                  key={sd}
                  className={`rounded-lg border border-l-4 border-slate-100 bg-slate-50/50 px-3 py-2.5 ${subdomainBorderColors[sd]}`}
              >
                <p className='mb-1.5 text-xs font-semibold text-slate-700'>{ sd }</p>
                <div className='grid grid-cols-2 gap-2'>
                  <div>
                    <label className='mb-0.5 block text-[11px] text-slate-400'>
                      Min
                    </label>
                    <input
                        type='number'
                        min={0}
                        max={100}
                        placeholder='Any'
                        value={filters.subdomainScores[sd].min}
                        onChange={(e) => setSubdomainMin(sd, e.target.value)}
                        className={inputClass}
                    />
                  </div>
                  <div>
                    <label className='mb-0.5 block text-[11px] text-slate-400'>
                      Max
                    </label>
                    <input
                        type='number'
                        min={0}
                        max={100}
                        placeholder='Any'
                        value={filters.subdomainScores[sd].max}
                        onChange={(e) => setSubdomainMax(sd, e.target.value)}
                        className={inputClass}
                    />
                  </div>
                </div>
              </div>
            )) }
          </div>
        </div>
      </div>

      { /* Footer actions */ }
      <div className='border-t border-slate-100 pt-4 flex items-center justify-between gap-3'>
        <button
            type='button'
            onClick={handleReset}
            className='text-sm font-medium text-slate-500 hover:text-rose-600 transition-colors'
        >
          Reset All
        </button>
        <div className='flex gap-2'>
          <Button label='Cancel' onClick={onApply} variant='secondary' className='' />
          <Button label='Apply Filters' onClick={onApply} variant='primary' className='' />
        </div>
      </div>
    </div>
  );
};
